import fetch from 'unfetch';
import { feature as topo2geo } from 'topojson-client';
import polylabel from '@mapbox/polylabel';
import * as D3dsv from 'd3-dsv'

import {
  numColumns, partyColumns, invalidVotesColumn, invalidVotesColumnComp, validVotesColumn, getPartyName, Voters,
  validVotesColumnComp, allVotesColumn, allVotesColumnComp, partyConfig, topParties, getPartyColor, countStatus, cityAgs
} from './config';
import { sortByKey, isNumeric } from '~/core/scripts/utils';

function parseDetailItems(data) {
  if (!data) {
    return [];
  }

  const items = data.votes || [];

  return items.map(item => ({
    name: item.person,
    party: item.name,
    value: item.votes,
    valueRel: item.votes / data.valid * 100
  }));
}

function getDetailLabel(ags) {
  if (cityAgs.includes(ags)) {
    return 'Oberbürgermeisterwahl';
  }
  return 'Landratswahl';
}

function mergeData(features, data, data2) {
  return features.map(feat => {
    const id = feat.properties.Wahlgebiet_ID.toString();
    const matchingData = data.find(d => d.Wahlgebiet_ID.toString() === id);
    const matchingData2 = data2.find(d => d.ags.toString() === id);

    feat.properties = {
      ...feat.properties,
      ...matchingData,
      detailItems: parseDetailItems(matchingData2),
      detailLabel: getDetailLabel(id)
    };

    return feat;
  });
}

function parseData(features) {
  const parsedFeatures = features.map(feat => {
    numColumns.forEach(col => {
      if (feat.properties[col]) {
        feat.properties[col] = +feat.properties[col];
      }
    });

    const partyItems = partyColumns.reduce((res, col) => {
      res.push({
        name: col,
        value: feat.properties[col],
        valueComp: feat.properties[`${col}_Vergleich`],
        color: partyConfig[col] ? partyConfig[col] : partyConfig.default,
        valueRel: typeof feat.properties[countStatus] === 'undefined' || feat.properties[countStatus] === 1 ? (feat.properties[col] / feat.properties[validVotesColumn]) * 100 : 0,
        //(feat.properties[col] / feat.properties[validVotesColumn]) * 100 | 0,
        valueRelComp: (feat.properties[`${col}_Vergleich`] / feat.properties[validVotesColumnComp]) * 100
      });

      return res;
    }, []).sort((a, b) => b.valueRel - a.valueRel);

    feat.properties.winnerItemComp = partyItems[0];

    const coords = feat.geometry.type === 'MultiPolygon' ? [feat.geometry.coordinates[0][0]] : [feat.geometry.coordinates[0]];
    const centroid = polylabel(coords, 1.0);

    feat.properties.partyItems = partyItems;
    feat.properties.centroid = centroid.reverse();
    feat.properties.isClose = Math.abs(partyItems[0].valueRel - partyItems[1].valueRel) <= 1;
    feat.properties.turnout = ((feat.properties[Voters]) / feat.properties[allVotesColumn]) * 100;
    feat.properties.turnoutComp = ((feat.properties[invalidVotesColumnComp] + feat.properties[validVotesColumnComp]) / feat.properties[allVotesColumnComp]) * 100;
    feat.properties.hasData = typeof feat.properties[countStatus] !== 'undefined' || feat.properties[countStatus] === 1;
    feat.properties.nonVotersRel = 100 - feat.properties.turnout;
    feat.properties.dissolveKey = feat.properties.Wahlgebiet_ID.toString().substr(0, +baseconfig.app.dissolveSubstr);

    const topPartyItems = partyItems.filter(item => topParties.includes(item.name));
    const othersItem = topPartyItems.reduce((res, item) => {
      res.valueRel -= item.valueRel;
      return res;
    }, {
      valueRel: 100
    });

    feat.properties.othersItem = othersItem;

    return feat;
  });

  return parsedFeatures;
}

export function calculateSonstige(data) {
  var sonst = data.reduce((res, item) => {
    res.valueRel -= item.valueRel;
    res.valueRelComp -= item.valueRelComp;
    return res;
  }, {
    name: 'sonstige',
    valueRel: 100,
    valueRelComp: 100,
    color: getPartyColor('sonstige')
  });
  if (sonst.valueRel === 100) sonst.valueRel = 0;
  return sonst;
}

export function calculateNonVoters(features) {
  const item = features.reduce((res, item) => {
    res.count++;
    res.valueSum += item.properties.nonVotersRel;

    return res;
  }, {
    name: 'nichtwaehler',
    valueRel: 0,
    valueSum: 0,
    count: 0,
    color: getPartyColor('nichtwaehler')
  });

  return {
    ...item,
    valueRel: item.valueSum / item.count
  };
}

async function getTopojson() {
  const response = await fetch(`data/nrw_krs_plus_aachen.topo.json`);
  const topojson = await response.json();

  return topojson;
}

async function getCSV() {
  const res = await fetch(`${ARTICLE_CONFIG.baseUrl}/data/nrwwahl/nrw_krs.csv`);
  //const res = await fetch(`data/nrw_krs_dummy.csv`);
  const csvString = await res.text();
  return D3dsv.csvParse(csvString); //.filter(d => d.Wahlbezirksart !== 'Briefwahlbezirk') ;
}

async function getJSON() {
  const res = await fetch(`${ARTICLE_CONFIG.baseUrl}/data/nrwwahl/nrw_gmd.json`);
  const json = await res.json();
  return json;
}

export async function loadData() {
  const topojson = await getTopojson();
  const topoKey = Object.keys(topojson.objects)[0];
  const geojson = topo2geo(topojson, topojson.objects[topoKey]);
  const data = await getCSV();
  const data2 = await getJSON();

  geojson.features = mergeData(geojson.features, data, data2)
  geojson.features = parseData(geojson.features);

  // const ovRes = await fetch('data/thueringen_wahlkreise.topo.json');
  // const ovTopo = await ovRes.json();
  // const ovTopoKey = Object.keys(ovTopo.objects)[0];
  // const overlayGeojson = topo2geo(ovTopo, ovTopo.objects[ovTopoKey]);

  return {
    geojson,
    topojson
  };
}

const parseRow = row => ({
  name: row.party_name,
  valueRel: +row.result,
  valueRelComp: +row.result_comp,
  color: getPartyColor(row.party_name)
});

export async function loadOverallData() {
  const res = await fetch(`${ARTICLE_CONFIG.baseUrl}/data/nrwwahl/nrw_detail.csv`);
  const csvString = await res.text();
  const data = D3dsv.csvParse(csvString, parseRow);

  data.sort(sortByKey('valueRel'));

  return data;
}

export async function loadTurnoutData() {
  const res = await fetch(`${ARTICLE_CONFIG.baseUrl}/data/nrwwahl/nrw_trunout.json`);
  const json = await res.json();
  return json.turnout;
}

export function hexToRgb(hex) {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, function(m, r, g, b) {
    return r + r + g + g + b + b;
  });

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (!result) {
    return null;
  }

  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);

  return { r, g, b, rgbString: `${r},${g},${b}` };
}